import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function useChatSocket({ threadId, onEvent }) {
  const { t } = useTranslation();

  const wsRef = useRef(null);
  const onEventRef = useRef(onEvent);

  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);

  const pingRef = useRef(null);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!threadId) return;

    stoppedRef.current = false;

    const connect = () => {
      if (stoppedRef.current) return;

      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const host = window.location.hostname;

      const token = localStorage.getItem("token");
      const qs = token ? `?token=${encodeURIComponent(token)}` : "";
      const wsUrl = `${proto}://${host}:8000/ws/chat/${threadId}${qs}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        console.log(t("chat_socket_open_log"), wsUrl);

        if (pingRef.current) clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          try {
            ws.send(JSON.stringify({ type: "ping" }));
          } catch {}
        }, 25000);
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          onEventRef.current?.(data);
        } catch {
          // ignore
        }
      };

      ws.onclose = (ev) => {
        console.log(t("chat_socket_close_log"), {
          code: ev.code,
          reason: ev.reason,
          wasClean: ev.wasClean,
        });

        if (pingRef.current) {
          clearInterval(pingRef.current);
          pingRef.current = null;
        }

        if (ev.code === 1008) {
          console.warn(t("chat_socket_stop_reconnect_1008"));
          return;
        }

        if (stoppedRef.current) return;

        const retry = Math.min(10, retryRef.current + 1);
        retryRef.current = retry;

        const delay = Math.min(15000, 500 * Math.pow(2, retry - 1));
        timerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch {}
      };
    };

    connect();

    return () => {
      stoppedRef.current = true;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (pingRef.current) {
        clearInterval(pingRef.current);
        pingRef.current = null;
      }

      try {
        wsRef.current?.close();
      } catch {}

      wsRef.current = null;
    };
  }, [threadId, t]);

  const send = (payload) => {
    try {
      wsRef.current?.send(JSON.stringify(payload));
    } catch {}
  };

  return { send };
}